import { useCallback, useEffect, useState } from "react";
import { useComfort } from "../ComfortContext";
import { getDictionary } from "../../i18n/dictionary";
import {
  buildMemberRows,
  FIXTURE_PENDING_INVITES,
  type MemberRow,
  type PendingInvite,
} from "./data";
import type { MembershipRole } from "../../domain/types";
import { getPortalData, shouldUseSupabase } from "../../data";
import { PortalWriteError } from "../../data/supabaseAdapter";
import { useOrg } from "./OrgContext";
import { useOrgProfile } from "./orgProfile";
import { useDemoVariant } from "./ui";

// ---- Error copy -------------------------------------------------------------
//
// Branch on the Postgres SQLSTATE the RPC raised, never on message text:
// 42501 = not authorized, P0001 = a rule was broken, P0002 = not found.
// (House style: see createOrgErrorCopy in ../auth/CreateOrganisation.tsx.)
// English-only, like every other write-error mapper in the portal.

/** Why an invitation could not be recorded. */
export function inviteErrorCopy(err: unknown): string {
  const code = err instanceof PortalWriteError ? err.code : undefined;
  const msg = err instanceof Error ? err.message.toLowerCase() : "";

  if (code === "42501") {
    return "Only an admin can invite people. Your session may have expired — please sign in again.";
  }
  if (code === "P0002") {
    return "We couldn't find your organisation. Please refresh the page and try again.";
  }
  if (code === "P0001") {
    if (msg.includes("already a member")) {
      return "That person is already a member of your organisation.";
    }
    if (msg.includes("email")) {
      return "That doesn't look like an email address. Please check it and try again.";
    }
    return "We couldn't record that invitation. Please check the address and try again.";
  }
  return "We couldn't record that invitation. Please try again in a moment.";
}

/** Why a role change was refused. */
export function roleErrorCopy(err: unknown): string {
  const code = err instanceof PortalWriteError ? err.code : undefined;

  if (code === "42501") {
    return "Only an admin can change roles. Your session may have expired — please sign in again.";
  }
  if (code === "P0002") {
    return "We couldn't find that member. Refresh the page and try again.";
  }
  if (code === "P0001") {
    // set_member_role raises P0001 for exactly two rules, and "invalid role" is
    // unreachable from a two-option select — so this is always the last-admin
    // guard, which deserves specific, actionable copy.
    return "Your organisation needs at least one admin. Make someone else an admin first, then change this one.";
  }
  return "We couldn't change that role. Please try again in a moment.";
}

/** Why the member list could not be read. */
/**
 * Copy for a failed remove-member / cancel-invitation. `remove_member` raises
 * P0001 for the last-admin guard and 42501 when the caller is not an admin;
 * both are actionable, so neither should be swallowed.
 */
export function removeErrorCopy(
  err: unknown,
  kind: "member" | "invite",
): string {
  const code = err instanceof PortalWriteError ? err.code : undefined;
  const msg = err instanceof Error ? err.message.toLowerCase() : "";
  if (code === "42501") {
    return "You no longer have admin rights on this organisation.";
  }
  if (code === "P0002") {
    return kind === "member"
      ? "That person is no longer a member — the list may be out of date."
      : "That invitation is no longer pending.";
  }
  if (code === "P0001") {
    if (msg.includes("at least one admin")) {
      return "Your organisation needs at least one admin. Make someone else an admin first, then remove this one.";
    }
    return "We couldn't do that. Please refresh and try again.";
  }
  return "Something went wrong on our side. Please try again in a moment.";
}

export function readErrorCopy(err: unknown): string {
  const code = err instanceof PortalWriteError ? err.code : undefined;
  if (code === "42501") {
    return "You need to be an admin of this organisation to see its members.";
  }
  return "We couldn't load your members just now. Nothing has changed — this is a reading problem, not your data.";
}

// ---- Local strings missing from the shared dictionary -----------------------
// (dictionary.ts is locked for this task; these will migrate there with i18n pass)

const L = {
  en: {
    emailLabel: "Email address",
    roleLabel: "Role",
    roleAdmin: "Admin",
    roleMember: "Member",
    statusActive: "Active",
    statusPending: "Pending",
    invitePending: "Invite pending",
    noSeat: "—",
    seatActive: "Assigned",
    seatPending: "Pending",
    removeAction: "Remove",
    cancelInvite: "Cancel invite",
    confirmRemoveTitle: "Remove this member?",
    confirmRemoveBody: "This will remove their access to the organisation.",
    confirmRemoveNote:
      "Their seat is freed immediately. They can be re-invited later.",
    confirmRemoveLabel: "Remove member",
    confirmCancelInviteTitle: "Cancel this invite?",
    confirmCancelInviteBody:
      "They will no longer join your organisation when they sign in.",
    confirmCancelInviteLabel: "Cancel invite",
    // HONEST COPY. There is no invitation email — no Edge Function sends one.
    // inviteMember() records a row; accept_member_invitations() turns it into a
    // membership the next time that person signs in at this portal. The admin
    // has to tell them, so the screen has to say so.
    inviteHelp:
      "We don't send an invitation email. After you invite someone, ask them to sign in here with that same email address — they'll join your organisation automatically.",
    inviteSuccessNote:
      "Invitation saved. No email goes out, so tell them to sign in here with that email address — they'll join automatically.",
    inviteErrorEmpty: "Enter an email address to record an invite.",
    actions: "Actions",
    colActions: "Actions",
    roleForLabel: "Role for",
    roleUpdated: "Role updated.",
    loadingLabel: "Loading your members…",
    retry: "Try again",
    emptyTitle: "No one else here yet",
    emptyBody: "Invite a colleague above and they'll appear here once they sign in.",
  },
  ms: {
    emailLabel: "Alamat e-mel",
    roleLabel: "Peranan",
    roleAdmin: "Pentadbir",
    roleMember: "Ahli",
    statusActive: "Aktif",
    statusPending: "Tertunda",
    invitePending: "Jemputan tertunda",
    noSeat: "—",
    seatActive: "Ditetapkan",
    seatPending: "Tertunda",
    removeAction: "Buang",
    cancelInvite: "Batal jemputan",
    confirmRemoveTitle: "Buang ahli ini?",
    confirmRemoveBody: "Ini akan membuang akses mereka kepada organisasi.",
    confirmRemoveNote:
      "Kerusi mereka dibebaskan serta-merta. Mereka boleh dijemput semula kemudian.",
    confirmRemoveLabel: "Buang ahli",
    confirmCancelInviteTitle: "Batalkan jemputan ini?",
    confirmCancelInviteBody:
      "Mereka tidak lagi akan menyertai organisasi anda apabila log masuk.",
    confirmCancelInviteLabel: "Batal jemputan",
    inviteHelp:
      "Kami tidak menghantar e-mel jemputan. Selepas anda menjemput seseorang, minta mereka log masuk di sini dengan alamat e-mel yang sama — mereka akan menyertai organisasi anda secara automatik.",
    inviteSuccessNote:
      "Jemputan disimpan. Tiada e-mel dihantar, jadi beritahu mereka untuk log masuk di sini dengan alamat e-mel itu — mereka akan menyertai secara automatik.",
    inviteErrorEmpty: "Masukkan alamat e-mel untuk merekod jemputan.",
    actions: "Tindakan",
    colActions: "Tindakan",
    roleForLabel: "Peranan untuk",
    roleUpdated: "Peranan dikemas kini.",
    loadingLabel: "Memuatkan ahli anda…",
    retry: "Cuba lagi",
    emptyTitle: "Belum ada sesiapa lagi di sini",
    emptyBody:
      "Jemput rakan sekerja di atas dan mereka akan muncul di sini sebaik sahaja log masuk.",
  },
} as const;

// ---- Confirm modal -----------------------------------------------------------

function ConfirmModal({
  title,
  body,
  note,
  confirmLabel,
  cancelLabel,
  /** Set when the confirmed action FAILED. The dialog stays open so the admin
   *  reads why, rather than watching it close with nothing changed. */
  error,
  busy,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string;
  note: string;
  confirmLabel: string;
  cancelLabel: string;
  error?: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="members-confirm-title"
      style={{
        position: "fixed",
        inset: 0,
        display: "grid",
        placeItems: "center",
        background: "rgba(0,0,0,.35)",
        zIndex: 1000,
        padding: 20,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          background: "var(--surface)",
          borderRadius: "var(--r-xl, 20px)",
          padding: "28px 24px",
          width: "100%",
          maxWidth: 440,
          boxShadow: "0 8px 32px rgba(0,0,0,.18)",
        }}
      >
        <h2
          id="members-confirm-title"
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: 20,
            color: "var(--ink-deep)",
            margin: "0 0 10px",
          }}
        >
          {title}
        </h2>
        <p style={{ fontSize: 15, color: "var(--ink-soft)", lineHeight: 1.6, margin: "0 0 4px" }}>
          {body}
        </p>
        <div
          style={{
            background: "var(--peach-tint)",
            border: "1px solid var(--peach)",
            borderRadius: "var(--r-lg)",
            padding: "13px 15px",
            margin: "14px 0 22px",
          }}
        >
          <p style={{ fontSize: 13.5, color: "var(--peach-deep)", margin: 0, lineHeight: 1.55 }}>
            {note}
          </p>
        </div>
        {/* The action failed. Announced, and the dialog stays open. */}
        <div role="status" aria-live="polite">
          {error ? (
            <p
              style={{
                margin: "0 0 18px",
                padding: "12px 14px",
                background: "var(--peach-tint)",
                color: "var(--peach-deep)",
                borderRadius: "var(--r-md)",
                fontSize: 14.5,
                lineHeight: 1.55,
              }}
            >
              {error}
            </p>
          ) : null}
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            onClick={onCancel}
            style={{
              minHeight: 44,
              padding: "0 20px",
              border: "1.5px solid var(--line)",
              background: "transparent",
              color: "var(--ink-soft)",
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: 14,
              borderRadius: "var(--r-pill)",
              cursor: "pointer",
            }}
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            style={{
              minHeight: 44,
              padding: "0 20px",
              border: 0,
              background: busy ? "var(--line-strong)" : "var(--peach)",
              color: busy ? "var(--ink-faint)" : "var(--peach-deep)",
              fontFamily: "var(--font-body)",
              fontWeight: 700,
              fontSize: 14,
              borderRadius: "var(--r-pill)",
              cursor: busy ? "default" : "pointer",
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Role badge --------------------------------------------------------------

function RoleBadge({ role, label }: { role: MembershipRole; label: string }) {
  const isAdmin = role === "admin";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 10px",
        borderRadius: "var(--r-pill)",
        background: isAdmin ? "var(--brand-tint)" : "var(--sky-tint)",
        color: isAdmin ? "var(--brand)" : "var(--sky-deep)",
        fontSize: 12.5,
        fontWeight: 600,
        letterSpacing: ".04em",
      }}
    >
      {label}
    </span>
  );
}

// ---- Status badge ------------------------------------------------------------

function StatusBadge({
  status,
  labels,
}: {
  status: "active" | "pending" | "invite";
  labels: { active: string; pending: string; invite: string };
}) {
  const isActive = status === "active";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 10px",
        borderRadius: "var(--r-pill)",
        background: isActive ? "var(--sage-tint)" : "var(--peach-tint)",
        color: isActive ? "var(--sage-deep)" : "var(--peach-deep)",
        fontSize: 12.5,
        fontWeight: 600,
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: isActive ? "var(--good)" : "var(--peach-deep)",
          display: "inline-block",
          flexShrink: 0,
        }}
      />
      {status === "active" ? labels.active : status === "pending" ? labels.pending : labels.invite}
    </span>
  );
}

// ---- Table cell / header helpers --------------------------------------------

const TH: React.CSSProperties = {
  padding: "10px 14px",
  textAlign: "left",
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: ".1em",
  textTransform: "uppercase",
  color: "var(--ink-faint)",
  borderBottom: "1px solid var(--line)",
  whiteSpace: "nowrap",
};

const TD: React.CSSProperties = {
  padding: "14px 14px",
  fontSize: 14.5,
  color: "var(--ink)",
  borderBottom: "1px solid var(--line)",
  verticalAlign: "middle",
};

// ---- Avatar initial ---------------------------------------------------------

function AvatarInitial({ email }: { email: string }) {
  const letter = email.charAt(0).toUpperCase();
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-grid",
        placeItems: "center",
        width: 34,
        height: 34,
        borderRadius: "50%",
        background: "var(--brand-tint)",
        color: "var(--brand)",
        fontFamily: "var(--font-display)",
        fontWeight: 700,
        fontSize: 14,
        flexShrink: 0,
        border: "1px solid var(--line)",
      }}
    >
      {letter}
    </span>
  );
}

// ---- Main export -------------------------------------------------------------

type ConfirmTarget =
  | { kind: "member"; accountId: string; email: string }
  | { kind: "invite"; id: string; email: string };

interface MembersViewProps {
  rows: MemberRow[];
  pendingInvites: PendingInvite[];
  onInvite: (email: string, role: MembershipRole) => Promise<void> | void;
  onRemove: (accountId: string) => Promise<void> | void;
  onCancelInvite: (id: string) => Promise<void> | void;
  /**
   * Live only. When present the Role column becomes a control wired to
   * set_member_role; when absent (demo) it stays a read-only badge.
   */
  onSetRole?: (accountId: string, role: MembershipRole) => Promise<void> | void;
  /** Live only: the FIRST read is still in flight — show neither rows nor "empty". */
  loading?: boolean;
  /** Live only: the read failed. An unknown list must never render as an empty one. */
  loadError?: string;
  /** Live only: re-runs the failed read. */
  onRetry?: () => void;
}

function MembersView({
  rows,
  pendingInvites,
  onInvite,
  onRemove,
  onCancelInvite,
  onSetRole,
  loading = false,
  loadError = "",
  onRetry,
}: MembersViewProps) {
  const { locale } = useComfort();
  const dict = getDictionary(locale);
  const t = dict.admin.members;
  const common = dict.common;
  const l = L[locale];
  const u = dict.admin.users;
  const profile = useOrgProfile();
  const solo = profile.kind === "individual" && rows.length <= 1 && pendingInvites.length === 0;
  const [showInvite, setShowInvite] = useState(false);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<MembershipRole>("member");
  const [inviteError, setInviteError] = useState("");
  const [inviteSent, setInviteSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const [confirmTarget, setConfirmTarget] = useState<ConfirmTarget | null>(null);
  const [actionError, setActionError] = useState("");
  const [roleBusyId, setRoleBusyId] = useState<string | null>(null);
  const [roleNotice, setRoleNotice] = useState<{
    accountId: string;
    text: string;
    tone: "ok" | "attention";
  } | null>(null);

  async function handleInvite() {
    const email = inviteEmail.trim();
    if (!email) {
      setInviteError(l.inviteErrorEmpty);
      return;
    }
    if (busy) return;
    setBusy(true);
    setInviteError("");
    try {
      await onInvite(email, inviteRole);
      setInviteEmail("");
      setInviteRole("member");
      setInviteSent(true);
    } catch (err) {
      setInviteError(inviteErrorCopy(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleRoleChange(row: MemberRow, next: MembershipRole) {
    if (!onSetRole || next === row.role || roleBusyId) return;
    setRoleBusyId(row.accountId);
    setRoleNotice(null);
    try {
      await onSetRole(row.accountId, next);
      setRoleNotice({ accountId: row.accountId, text: l.roleUpdated, tone: "ok" });
    } catch (err) {
      // The select is controlled by row.role, so a refusal leaves it showing
      // the role the server still holds — no optimistic lie to undo.
      setRoleNotice({
        accountId: row.accountId,
        text: roleErrorCopy(err),
        tone: "attention",
      });
    } finally {
      setRoleBusyId(null);
    }
  }

  function handleRemoveMember(row: MemberRow) {
    setConfirmTarget({ kind: "member", accountId: row.accountId, email: row.email });
  }

  function handleCancelInvite(invite: PendingInvite) {
    setConfirmTarget({ kind: "invite", id: invite.id, email: invite.email });
  }

  async function handleConfirm() {
    if (!confirmTarget || busy) return;
    setBusy(true);
    setActionError("");
    try {
      if (confirmTarget.kind === "member") {
        await onRemove(confirmTarget.accountId);
      } else {
        await onCancelInvite(confirmTarget.id);
      }
      setConfirmTarget(null);
    } catch (err) {
      // NOT best-effort. remove_member refuses to strip the last admin (P0001)
      // and refuses a non-admin caller (42501). Swallowing that closed the
      // dialog and left the member in the list with no explanation — the admin
      // could only conclude the screen was broken. Keep the dialog open, say
      // what happened, and let them try again or cancel.
      setActionError(removeErrorCopy(err, confirmTarget.kind));
    } finally {
      setBusy(false);
    }
  }

  const confirmProps =
    confirmTarget?.kind === "member"
      ? {
          title: l.confirmRemoveTitle,
          body: `${l.confirmRemoveBody.replace("their", confirmTarget.email + "'s")}`,
          note: l.confirmRemoveNote,
          confirmLabel: l.confirmRemoveLabel,
        }
      : confirmTarget?.kind === "invite"
        ? {
            title: l.confirmCancelInviteTitle,
            body: l.confirmCancelInviteBody,
            note: confirmTarget.email,
            confirmLabel: l.confirmCancelInviteLabel,
          }
        : null;

  return (
    <>
      {/* Page header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 20, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ fontSize: 30, color: "var(--ink-deep)", letterSpacing: "-.02em", margin: 0 }}>{u.title}</h1>
          <p style={{ color: "var(--ink-soft)", marginTop: 8, fontSize: 16.5, margin: "8px 0 0" }}>{solo ? u.soloSubtitle : t.subtitle}</p>
          {!solo && <p style={{ color: "var(--ink-faint)", fontSize: 14, margin: "6px 0 0" }}>{u.seatsFree}</p>}
        </div>
      </div>

      {/* An individual practitioner on their own: no team wording, and the
          invite form waits until they ask for it. */}
      {solo && !showInvite && (
        <div
          style={{
            marginTop: 24,
            background: "var(--surface)",
            border: "1px solid var(--line)",
            borderRadius: "var(--r-lg)",
            boxShadow: "var(--sh-1)",
            padding: 20,
          }}
        >
          <h2 style={{ fontSize: 17, fontWeight: 700, color: "var(--ink-deep)", margin: 0 }}>{u.justYouTitle}</h2>
          <p style={{ color: "var(--ink-soft)", fontSize: 15, lineHeight: 1.55, margin: "6px 0 14px" }}>{u.justYouBody}</p>
          <button
            type="button"
            onClick={() => setShowInvite(true)}
            style={{ minHeight: 44, padding: "0 18px", border: "1.5px solid var(--line-strong)", background: "var(--surface)", color: "var(--ink)", borderRadius: "var(--r-pill)", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 14.5, cursor: "pointer" }}
          >
            {u.inviteColleague}
          </button>
        </div>
      )}

      {/* Invite panel */}
      {(!solo || showInvite) && (
      <div
        style={{
          marginTop: 24,
          background: "var(--surface)",
          border: "1px solid var(--line)",
          borderRadius: "var(--r-lg)",
          boxShadow: "var(--sh-1)",
          padding: 20,
        }}
      >
        <h2 style={{ fontSize: 16, fontWeight: 700, color: "var(--ink-deep)", margin: "0 0 14px" }}>
          {t.inviteButton}
        </h2>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          {/* Email input */}
          <div style={{ flex: "1 1 260px", display: "flex", flexDirection: "column", gap: 5 }}>
            <label
              htmlFor="invite-email"
              style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-soft)" }}
            >
              {l.emailLabel}
            </label>
            <input
              id="invite-email"
              type="email"
              value={inviteEmail}
              onChange={(e) => {
                setInviteEmail(e.target.value);
                if (inviteError) setInviteError("");
                if (inviteSent) setInviteSent(false);
              }}
              placeholder="name@organisation.org"
              style={{
                height: 44,
                padding: "0 14px",
                border: `1.5px solid ${inviteError ? "var(--peach-deep)" : "var(--line)"}`,
                borderRadius: "var(--r-md)",
                background: "var(--surface)",
                color: "var(--ink)",
                fontFamily: "var(--font-body)",
                fontSize: 14.5,
                outline: "none",
              }}
            />
          </div>

          {/* Role select */}
          <div style={{ flex: "0 1 160px", display: "flex", flexDirection: "column", gap: 5 }}>
            <label
              htmlFor="invite-role"
              style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-soft)" }}
            >
              {l.roleLabel}
            </label>
            <select
              id="invite-role"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as MembershipRole)}
              style={{
                height: 44,
                padding: "0 14px",
                border: "1.5px solid var(--line)",
                borderRadius: "var(--r-md)",
                background: "var(--surface)",
                color: "var(--ink)",
                fontFamily: "var(--font-body)",
                fontSize: 14.5,
                cursor: "pointer",
              }}
            >
              <option value="member">{l.roleMember}</option>
              <option value="admin">{l.roleAdmin}</option>
            </select>
          </div>

          {/* Invite button */}
          <button
            onClick={() => void handleInvite()}
            disabled={busy}
            style={{
              flex: "0 0 auto",
              minHeight: 44,
              padding: "0 22px",
              border: 0,
              borderRadius: "var(--r-pill)",
              background: "var(--brand)",
              color: "#fff",
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: 15,
              cursor: "pointer",
              boxShadow: "var(--sh-1)",
              alignSelf: "flex-end",
            }}
          >
            {t.inviteButton}
          </button>
        </div>

        {/* What actually happens — said before they press the button, not after. */}
        <p
          style={{
            margin: "12px 0 0",
            fontSize: 13.5,
            lineHeight: 1.55,
            color: "var(--ink-faint)",
          }}
        >
          {l.inviteHelp}
        </p>

        {/* Feedback — a persistent live region so the outcome is announced. */}
        <div role="status" aria-live="polite">
          {inviteError && (
            <p style={{ fontSize: 13.5, color: "var(--peach-deep)", margin: "8px 0 0" }}>
              {inviteError}
            </p>
          )}
          {inviteSent && !inviteError && (
            <p style={{ fontSize: 13.5, color: "var(--sage-deep)", margin: "8px 0 0", lineHeight: 1.55 }}>
              {l.inviteSuccessNote}
            </p>
          )}
        </div>
      </div>
      )}

      {/* Members table.
          Loading and failure are DISTINCT from "there is nobody here": a read
          that is in flight, or one that failed, must never be drawn as an empty
          organisation. */}
      <div
        style={{
          marginTop: 18,
          background: "var(--surface)",
          border: "1px solid var(--line)",
          borderRadius: "var(--r-lg)",
          boxShadow: "var(--sh-1)",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <div
            role="status"
            aria-live="polite"
            style={{
              padding: "42px 24px",
              textAlign: "center",
              color: "var(--ink-faint)",
              fontSize: 15,
            }}
          >
            {l.loadingLabel}
          </div>
        ) : loadError ? (
          <div
            role="status"
            aria-live="polite"
            style={{ padding: "34px 24px", textAlign: "center" }}
          >
            <p
              style={{
                margin: "0 auto",
                maxWidth: 440,
                padding: "13px 15px",
                background: "var(--peach-tint)",
                color: "var(--peach-deep)",
                borderRadius: "var(--r-md)",
                fontSize: 14.5,
                lineHeight: 1.55,
              }}
            >
              {loadError}
            </p>
            {onRetry && (
              <button
                onClick={onRetry}
                style={{
                  marginTop: 16,
                  minHeight: 44,
                  padding: "0 22px",
                  border: "1.5px solid var(--line)",
                  background: "transparent",
                  color: "var(--ink-deep)",
                  fontFamily: "var(--font-body)",
                  fontWeight: 600,
                  fontSize: 14.5,
                  borderRadius: "var(--r-pill)",
                  cursor: "pointer",
                }}
              >
                {l.retry}
              </button>
            )}
          </div>
        ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
            <thead>
              <tr>
                <th scope="col" style={TH}>{t.colMember}</th>
                <th scope="col" style={TH}>{t.colRole}</th>
                <th scope="col" style={TH}>{t.colSeat}</th>
                <th scope="col" style={TH}>{t.colStatus}</th>
                <th scope="col" style={{ ...TH, textAlign: "right" }}>{l.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {/* Active members */}
              {rows.map((row) => {
                const isLastRow =
                  rows.indexOf(row) === rows.length - 1 && pendingInvites.length === 0;
                return (
                  <tr key={row.accountId}>
                    <td style={{ ...TD, borderBottom: isLastRow ? "none" : TD.borderBottom }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <AvatarInitial email={row.email} />
                        <span style={{ fontSize: 14.5, color: "var(--ink-deep)", fontWeight: 500 }}>
                          {row.email}
                        </span>
                      </div>
                    </td>
                    <td style={{ ...TD, borderBottom: isLastRow ? "none" : TD.borderBottom }}>
                      {onSetRole ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 6, maxWidth: 230 }}>
                          <select
                            aria-label={`${l.roleForLabel} ${row.email}`}
                            value={row.role}
                            disabled={roleBusyId === row.accountId}
                            onChange={(e) =>
                              void handleRoleChange(row, e.target.value as MembershipRole)
                            }
                            style={{
                              minHeight: 38,
                              padding: "0 10px",
                              border: "1.5px solid var(--line)",
                              borderRadius: "var(--r-md)",
                              background: "var(--surface)",
                              color: "var(--ink)",
                              fontFamily: "var(--font-body)",
                              fontSize: 14,
                              cursor: roleBusyId === row.accountId ? "default" : "pointer",
                            }}
                          >
                            <option value="member">{l.roleMember}</option>
                            <option value="admin">{l.roleAdmin}</option>
                          </select>
                          {/* Outcome of a role change lands here, asynchronously. */}
                          <div role="status" aria-live="polite">
                            {roleNotice?.accountId === row.accountId && (
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: 12.5,
                                  lineHeight: 1.5,
                                  color:
                                    roleNotice.tone === "ok"
                                      ? "var(--sage-deep)"
                                      : "var(--peach-deep)",
                                }}
                              >
                                {roleNotice.text}
                              </p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <RoleBadge role={row.role} label={row.role === "admin" ? l.roleAdmin : l.roleMember} />
                      )}
                    </td>
                    <td style={{ ...TD, borderBottom: isLastRow ? "none" : TD.borderBottom }}>
                      {row.hasSeat ? (
                        <span
                          style={{
                            fontSize: 13.5,
                            fontWeight: 600,
                            color: row.seatStatus === "pending" ? "var(--peach-deep)" : "var(--sage-deep)",
                          }}
                        >
                          {row.seatStatus === "pending" ? l.seatPending : l.seatActive}
                        </span>
                      ) : (
                        <span style={{ fontSize: 14.5, color: "var(--ink-faint)" }}>{l.noSeat}</span>
                      )}
                    </td>
                    <td style={{ ...TD, borderBottom: isLastRow ? "none" : TD.borderBottom }}>
                      <StatusBadge
                        status={row.seatStatus === "pending" ? "pending" : "active"}
                        labels={{ active: l.statusActive, pending: l.statusPending, invite: l.invitePending }}
                      />
                    </td>
                    <td
                      style={{
                        ...TD,
                        borderBottom: isLastRow ? "none" : TD.borderBottom,
                        textAlign: "right",
                      }}
                    >
                      <button
                        onClick={() => handleRemoveMember(row)}
                        aria-label={`Remove ${row.email}`}
                        style={{
                          minHeight: 36,
                          padding: "0 14px",
                          border: "1.5px solid var(--peach-deep)",
                          background: "transparent",
                          color: "var(--peach-deep)",
                          fontFamily: "var(--font-body)",
                          fontWeight: 600,
                          fontSize: 13,
                          borderRadius: "var(--r-pill)",
                          cursor: "pointer",
                        }}
                      >
                        {l.removeAction}
                      </button>
                    </td>
                  </tr>
                );
              })}

              {/* Pending invites */}
              {pendingInvites.map((invite, idx) => {
                const isLast = idx === pendingInvites.length - 1;
                return (
                  <tr key={invite.id} style={{ background: "var(--peach-tint, #fff9f5)" }}>
                    <td style={{ ...TD, borderBottom: isLast ? "none" : TD.borderBottom }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <AvatarInitial email={invite.email} />
                        <span style={{ fontSize: 14.5, color: "var(--ink)", fontWeight: 500 }}>
                          {invite.email}
                        </span>
                      </div>
                    </td>
                    <td style={{ ...TD, borderBottom: isLast ? "none" : TD.borderBottom }}>
                      <RoleBadge role={invite.role} label={invite.role === "admin" ? l.roleAdmin : l.roleMember} />
                    </td>
                    <td style={{ ...TD, borderBottom: isLast ? "none" : TD.borderBottom }}>
                      <span style={{ fontSize: 14.5, color: "var(--ink-faint)" }}>{l.noSeat}</span>
                    </td>
                    <td style={{ ...TD, borderBottom: isLast ? "none" : TD.borderBottom }}>
                      <StatusBadge
                        status="invite"
                        labels={{ active: l.statusActive, pending: l.statusPending, invite: l.invitePending }}
                      />
                    </td>
                    <td
                      style={{
                        ...TD,
                        borderBottom: isLast ? "none" : TD.borderBottom,
                        textAlign: "right",
                      }}
                    >
                      <button
                        onClick={() => handleCancelInvite(invite)}
                        aria-label={`Cancel invite for ${invite.email}`}
                        style={{
                          minHeight: 36,
                          padding: "0 14px",
                          border: "1.5px solid var(--peach-deep)",
                          background: "transparent",
                          color: "var(--peach-deep)",
                          fontFamily: "var(--font-body)",
                          fontWeight: 600,
                          fontSize: 13,
                          borderRadius: "var(--r-pill)",
                          cursor: "pointer",
                        }}
                      >
                        {l.cancelInvite}
                      </button>
                    </td>
                  </tr>
                );
              })}

              {/* Empty state — the ordinary day-one shape of a new organisation,
                  so it reads as calm and intentional, not broken. */}
              {rows.length === 0 && pendingInvites.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    style={{ ...TD, borderBottom: "none", textAlign: "center", paddingTop: 30, paddingBottom: 30 }}
                  >
                    <span style={{ display: "block", fontWeight: 600, color: "var(--ink-deep)", fontSize: 15.5 }}>
                      {l.emptyTitle}
                    </span>
                    <span style={{ display: "block", marginTop: 6, color: "var(--ink-faint)", fontSize: 14 }}>
                      {l.emptyBody}
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {/* Confirm modal */}
      {confirmTarget && confirmProps && (
        <ConfirmModal
          title={confirmProps.title}
          body={confirmProps.body}
          note={confirmProps.note}
          confirmLabel={confirmProps.confirmLabel}
          cancelLabel={common.cancel}
          error={actionError}
          busy={busy}
          onConfirm={() => void handleConfirm()}
          onCancel={() => {
            setActionError("");
            setConfirmTarget(null);
          }}
        />
      )}
    </>
  );
}

// ---- Dispatcher + demo/live wrappers -----------------------------------------

/** Real membership/seat writes when Supabase is configured, else the demo store. */
export function Members() {
  return shouldUseSupabase() ? <MembersLive /> : <MembersDemo />;
}

function MembersDemo() {
  // Remount per demo organisation so the individual practitioner starts on
  // their own and the centre starts with its team.
  const variant = useDemoVariant();
  return <MembersDemoFor key={variant} solo={variant === "individual"} />;
}

const DEMO_PRACTITIONER: MemberRow = {
  accountId: "acc-dr-aisyah",
  email: "dr.aisyah@example.com",
  role: "admin",
  hasSeat: true,
  seatStatus: "active",
};

function MembersDemoFor({ solo }: { solo: boolean }) {
  const [rows, setRows] = useState<MemberRow[]>(() => (solo ? [DEMO_PRACTITIONER] : buildMemberRows()));
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>(
    () => (solo ? [] : [...FIXTURE_PENDING_INVITES]),
  );
  return (
    <MembersView
      rows={rows}
      pendingInvites={pendingInvites}
      onInvite={(email, role) => {
        setPendingInvites((prev) => [
          ...prev,
          { id: `inv-${Date.now()}`, email, role, invitedAt: new Date().toISOString() },
        ]);
      }}
      onRemove={(accountId) => {
        setRows((prev) => prev.filter((r) => r.accountId !== accountId));
      }}
      onCancelInvite={(id) => {
        setPendingInvites((prev) => prev.filter((i) => i.id !== id));
      }}
    />
  );
}

/**
 * The real screen.
 *
 * Two things used to be untrue here. The org id was re-derived with
 * `memberships.find(...) ?? memberships[0] ?? ""` — which silently addressed
 * *someone else's* organisation, or the empty string. And the member list was
 * built from `memberships()` + `seats()`, which cannot carry an email: the
 * `account` RLS policy is self-only, so every row but your own rendered as an
 * opaque "Member ··1a2b". `org_members` is the admin-gated aggregate that
 * returns the real adult emails (adults, not children — child identity is never
 * exposed to an admin anywhere in this portal).
 */
function MembersLive() {
  const { orgId } = useOrg();
  const [rows, setRows] = useState<MemberRow[]>([]);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  /** `quiet` refreshes after a write without flashing the whole list away. */
  const load = useCallback(
    async (quiet = false) => {
      const data = getPortalData();
      if (!quiet) setLoading(true);
      setLoadError("");
      try {
        const [members, invites] = await Promise.all([
          data.orgMembers(orgId),
          data.memberInvitations(),
        ]);
        setRows(
          members.map((m) => ({
            accountId: m.accountId,
            email: m.email,
            role: m.role,
            hasSeat: m.seatStatus !== null,
            seatStatus: m.seatStatus,
          })),
        );
        setPendingInvites(
          invites
            .filter((iv) => iv.orgId === orgId)
            .map((iv) => ({
              id: iv.id,
              email: iv.email,
              role: iv.role,
              invitedAt: iv.createdAt,
            })),
        );
      } catch (err) {
        // A failed read is NOT an empty organisation. Say so, and keep the
        // retry within reach.
        //
        // A failed QUIET refresh is different: rows are already on screen and
        // still broadly true, so replacing the whole table with an error card
        // would destroy more information than it conveys. The write that
        // triggered the refresh reports its own outcome.
        if (!quiet) setLoadError(readErrorCopy(err));
      } finally {
        setLoading(false);
      }
    },
    [orgId],
  );

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <MembersView
      rows={rows}
      pendingInvites={pendingInvites}
      loading={loading}
      loadError={loadError}
      onRetry={() => void load()}
      onInvite={async (email, role) => {
        await getPortalData().inviteMember(orgId, email, role);
        await load(true);
      }}
      onRemove={async (accountId) => {
        await getPortalData().removeMember(orgId, accountId);
        await load(true);
      }}
      onCancelInvite={async (id) => {
        await getPortalData().cancelInvite(id);
        await load(true);
      }}
      onSetRole={async (accountId, role) => {
        await getPortalData().setMemberRole(orgId, accountId, role);
        await load(true);
      }}
    />
  );
}
